import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Info, X, ExternalLink, MessageCircle, Star, ChevronRight } from "lucide-react";

// Calibrated coordinates for the cosmography map artwork
const HOTSPOTS = [
    { slug: "andromeda",      left: 87, top: 14, color: "#f4c97a" },
    { slug: "pleiades",       left: 70, top: 28, color: "#7fb6ff" },
    { slug: "lyra",           left: 42, top: 35, color: "#c46cff" },
    { slug: "arcturus",       left: 24, top: 47, color: "#c46cff" },
    { slug: "zeta-reticuli",  left: 30, top: 58, color: "#c46cff" },
    { slug: "sirius",         left: 49, top: 65, color: "#c46cff" },
    { slug: "orion-rigel",    left: 78, top: 55, color: "#c46cff" },
    { slug: "draco",          left: 18, top: 32, color: "#e85b6f" },
    { slug: "local",          left: 50, top: 50, color: "#22c55e" },
    { slug: "interdimensional", left: 65, top: 85, color: "#a78bfa" },
];

// Draco constellation companion stars — a stylised arc of the main Draco shape (Thuban, Etamin, Rastaban, Eltanin)
// All positions relative to the main Draco hotspot, offsets in % units of map width/height.
const DRACO_COMPANIONS = [
    { dx: -4.5, dy: -2.5, size: 4 },
    { dx: -2.0, dy: -3.5, size: 5 },
    { dx: +2.5, dy: -2.0, size: 4 },
    { dx: +4.0, dy: +0.5, size: 3 },
    { dx: +1.5, dy: +2.5, size: 3 },
];

const STAR_MAP_URL = "https://customer-assets.emergentagent.com/job_witness-lodge/artifacts/uf2fkdsz_9C8599D1-AF0C-478F-9892-158C5B91F97A.png";

function slugify(s) { return (s || "").toLowerCase().replace(/\s+/g, "-"); }

export default function Universe() {
    const [systems, setSystems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [enrich, setEnrich] = useState(null);
    const [enrichLoading, setEnrichLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        Promise.resolve().then(async () => {
            try {
                const r = await api.get("/universe");
                if (!cancelled) setSystems(r.data.items || []);
            } catch (_) { /* ignore */ }
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        Promise.resolve().then(async () => {
            if (!selected) { if (!cancelled) setEnrich(null); return; }
            if (!cancelled) setEnrichLoading(true);
            try {
                const r = await api.get(`/universe/${selected.slug}`);
                if (!cancelled) setEnrich(r.data);
            } catch (_) { if (!cancelled) setEnrich(null); }
            if (!cancelled) setEnrichLoading(false);
        });
        return () => { cancelled = true; };
    }, [selected]);

    const bySlug = Object.fromEntries(systems.map((s) => [s.slug, s]));

    return (
        <div className="ok-container py-10" data-testid="universe-page">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-50">Cosmography · Reported Origins</div>
                    <h1 className="font-display text-3xl sm:text-4xl mt-1">Universe Map</h1>
                    <p className="opacity-70 max-w-2xl mt-2 text-sm">
                        The reported origin systems of every species in the archive. Click any point on the map to see the
                        species, encounters, and media tied to that system. All claims are catalogued as testimony, not fact.
                    </p>
                </div>
            </div>

            {loading ? (
                <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Star map */}
                    <div className="lg:col-span-2 ok-card relative overflow-hidden" data-testid="universe-starmap-wrapper" style={{ aspectRatio: "1 / 1", background: "#03060a" }}>
                        <img src={STAR_MAP_URL} alt="Stellar and galactic destinations relative to Earth" className="absolute inset-0 w-full h-full object-contain" />
                        {HOTSPOTS.map((h) => {
                            const sys = bySlug[h.slug];
                            const isActive = selected?.slug === h.slug;
                            const isDraco = h.slug === "draco";
                            return (
                                <div key={h.slug}>
                                    {/* Draco constellation companion stars (visual treatment to match Pleiades) */}
                                    {isDraco && DRACO_COMPANIONS.map((c, i) => (
                                        <span
                                            key={`draco-companion-${i}`}
                                            className="absolute rounded-full pointer-events-none"
                                            style={{
                                                left: `${h.left + c.dx}%`,
                                                top: `${h.top + c.dy}%`,
                                                width: c.size,
                                                height: c.size,
                                                background: "#e85b6f",
                                                boxShadow: `0 0 ${c.size * 2}px #e85b6faa`,
                                                opacity: 0.75,
                                                transform: "translate(-50%, -50%)",
                                            }}
                                        />
                                    ))}
                                    {/* Hotspot clickable marker */}
                                    <button
                                        onClick={() => setSelected(sys || null)}
                                        className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full transition-all"
                                        style={{
                                            left: `${h.left}%`, top: `${h.top}%`,
                                            width: isActive ? 44 : 30, height: isActive ? 44 : 30,
                                            background: "transparent",
                                            boxShadow: `0 0 0 2px ${h.color}, 0 0 ${isActive ? 28 : 14}px ${h.color}aa`,
                                            cursor: "pointer",
                                        }}
                                        title={sys?.name}
                                        aria-label={sys?.name}
                                        data-testid={`universe-hotspot-${h.slug}`}
                                    />
                                    {/* Draco label — Pleiades-style printed label adjacent to the marker */}
                                    {isDraco && sys && (
                                        <div
                                            className="absolute pointer-events-none font-mono uppercase tracking-wide"
                                            style={{
                                                left: `${h.left + 3.5}%`,
                                                top: `${h.top - 0.5}%`,
                                                color: "#f4c8cf",
                                                textShadow: "0 0 6px rgba(0,0,0,0.85)",
                                                lineHeight: 1.15,
                                            }}
                                        >
                                            <div style={{ fontSize: 11, letterSpacing: "0.08em", color: "#f4c8cf" }}>DRACO</div>
                                            <div style={{ fontSize: 9, opacity: 0.75, color: "#f4c8cf" }}>{sys.light_years} LY</div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {/* Detail panel */}
                    <aside className="ok-card p-5 max-h-[80vh] overflow-y-auto" data-testid="universe-detail-panel">
                        {!selected ? (
                            <div className="text-sm opacity-80" data-testid="universe-detail-empty">
                                <Info className="w-5 h-5 mb-2 text-[var(--ok-accent)]" />
                                <p className="mb-3">Click any glowing point on the map to view the species, cases, and media tied to that origin.</p>
                                <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mb-2">All systems ({systems.length})</div>
                                <ul className="space-y-1.5 text-xs">
                                    {systems.map((s) => {
                                        const hot = HOTSPOTS.find((h) => h.slug === s.slug);
                                        return (
                                            <li key={s.slug}>
                                                <button onClick={() => setSelected(s)} className="hover:text-[var(--ok-accent)] text-left flex items-center gap-2 w-full" data-testid={`universe-quicklink-${s.slug}`}>
                                                    <span style={{ color: hot?.color || "#888" }}>●</span>
                                                    <span className="flex-1">{s.name}</span>
                                                    <span className="opacity-50 font-mono">{s.light_years === null ? "non-spatial" : s.light_years === 0 ? "local" : `${s.light_years.toLocaleString()} ly`}</span>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ) : (
                            <div data-testid={`universe-detail-${selected.slug}`}>
                                <div className="flex items-start justify-between">
                                    <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: selected.speculation ? "#c46cff" : "var(--ok-accent)" }}>
                                        {selected.lore_category || (selected.speculation ? "Speculative origin" : "Confirmed local")} · {selected.constellation}
                                    </div>
                                    <button onClick={() => setSelected(null)} className="opacity-50 hover:opacity-100" aria-label="Close"><X className="w-4 h-4" /></button>
                                </div>
                                <h2 className="font-display text-2xl mt-1 mb-1">{selected.name}</h2>
                                <div className="text-xs font-mono opacity-60 mb-3">
                                    {selected.light_years === null
                                        ? "Non-spatial / interdimensional"
                                        : selected.light_years === 0
                                            ? "Local · Earth / Inner Earth"
                                            : `${selected.light_years.toLocaleString()} light years from Earth`}
                                </div>

                                <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mb-2">Overview</div>
                                <p className="text-sm opacity-90 leading-relaxed mb-5">{selected.overview || selected.notes}</p>

                                {selected.claimed_connections && selected.claimed_connections.length > 0 && (
                                    <>
                                        <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mb-2">Claimed connections</div>
                                        <ul className="text-xs opacity-80 mb-5 space-y-1">
                                            {selected.claimed_connections.map((cc, i) => <li key={i} className="flex items-start gap-1.5"><ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0 opacity-50" />{cc}</li>)}
                                        </ul>
                                    </>
                                )}

                                <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mb-2">Associated species</div>
                                <div className="flex flex-wrap gap-2 mb-5">
                                    {(selected.associated_species || []).map((sp) => (
                                        <Link key={sp} to={`/species/${slugify(sp)}`} className="ok-tag hover:border-[var(--ok-accent)] hover:text-[var(--ok-accent)]" data-testid={`universe-species-${slugify(sp)}`}>{sp}</Link>
                                    ))}
                                </div>

                                {enrichLoading ? (
                                    <div className="text-xs font-mono opacity-50 py-2">Loading connections…</div>
                                ) : enrich && (
                                    <>
                                        {(enrich.related_cases || []).length > 0 && (
                                            <>
                                                <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mb-2 mt-4">Related cases ({enrich.related_cases.length})</div>
                                                <div className="space-y-1.5 mb-5">
                                                    {enrich.related_cases.slice(0, 8).map((c) => (
                                                        <Link key={c.id} to={`/encounters/${c.id}`} className="block ok-card p-2.5 hover:border-[var(--ok-accent)] transition" data-testid={`universe-case-${c.id}`}>
                                                            <div className="text-xs font-medium truncate">{c.title}</div>
                                                            <div className="text-[10px] font-mono opacity-50 truncate flex items-center gap-2">
                                                                <span>{(c.encounter_date || "").slice(0,4)}</span>
                                                                <span>·</span>
                                                                <span className="truncate">{c.location || c.country || ""}</span>
                                                                {c.credibility_score ? <span className="ml-auto">★ {c.credibility_score}</span> : null}
                                                            </div>
                                                        </Link>
                                                    ))}
                                                </div>
                                            </>
                                        )}

                                        {(enrich.related_media || []).length > 0 && (
                                            <>
                                                <div className="font-mono text-[10px] uppercase tracking-widest opacity-50 mb-2">Related media ({enrich.related_media.length})</div>
                                                <div className="space-y-1.5 mb-5">
                                                    {enrich.related_media.slice(0, 5).map((m) => (
                                                        <Link key={m.slug} to={`/media-archive/${m.slug}`} className="block ok-card p-2.5 hover:border-[var(--ok-accent)] transition" data-testid={`universe-media-${m.slug}`}>
                                                            <div className="text-xs font-medium truncate">{m.title}</div>
                                                            <div className="text-[10px] font-mono opacity-50 truncate">{m.classification} · {m.reliability}</div>
                                                        </Link>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}

                                <div className="ok-card p-3 flex items-center gap-2 text-xs">
                                    <Info className="w-3.5 h-3.5 text-[var(--ok-accent)]" />
                                    <span><strong>{selected.report_count || 0}</strong> reported encounter{(selected.report_count || 0) === 1 ? "" : "s"} tied to species from this origin.</span>
                                </div>

                                {selected.speculation && (
                                    <div className="mt-4 text-[11px] opacity-50 italic leading-relaxed">
                                        Note: This origin is reported by witnesses and contactees. Project Unveiled catalogues claims as data, not as fact.
                                    </div>
                                )}
                            </div>
                        )}
                    </aside>
                </div>
            )}

            <p className="text-[10px] opacity-40 mt-4 font-mono uppercase tracking-widest">
                Positions are astronomically accurate · projection: spherical, Earth-centred · sources: NASA · ESA · SIMBAD · GAIA · HST
            </p>
        </div>
    );
}
